// ============================================================================
// FRAME CLAMPING ENGINE  v2  — Adaptive 100 / 1000 Rule
//
// Every animation sequence is clamped to a budget appropriate for its
// algorithm class:
//
//   TIGHT  (≤ 100 frames)
//     Simple, single-pass algorithms where >100 frames would create
//     visual noise rather than insight:
//       • Sorting  (bubble, insertion, selection, shell, cocktail …)
//       • Searching  (linear, binary, jump, interpolation …)
//       • Simple math  (sieve, gcd, fast-exp, fibonacci …)
//       • Geometry  (convex-hull, point ops …)
//
//   EXPANDED  (≤ 1000 frames)
//     Complex, multi-phase algorithms where truncating frames loses
//     semantically distinct algorithm stages:
//       • Graph  (Dijkstra, Bellman-Ford, Kruskal, Prim, Floyd-Warshall,
//                 BFS, DFS, topological sort, SCC, bridges …)
//       • Dynamic Programming  (LCS, knapsack, edit-distance, matrix-chain …)
//       • String matching  (KMP, Rabin-Karp, Z-function, Aho-Corasick …)
//       • Data Structures  (AVL/RB/B-tree rotations, segment tree range ops …)
//       • Backtracking  (N-queens, Hamilton cycle, knight's tour …)
//
// The resolver derives the tier from the algorithm category. The legacy
// algorithm-id lookup has been removed because category is now passed in
// correctly by the runner. If category is null or unknown, default to TIGHT.
// ============================================================================

import { ExecutionStep, StepType } from '../types/rust-engine-interfaces';

// ── Tier constants ────────────────────────────────────────────────────────────

/** Tight tier: short, high-density sequences. */
export const FRAME_MIN_TIGHT    = 20;
export const FRAME_MAX_TIGHT    = 100;

/** Expanded tier: long, multi-stage sequences. */
export const FRAME_MIN_EXPANDED = 25;
export const FRAME_MAX_EXPANDED = 1000;

/** Legacy-compatible aliases (default to tight values). */
export const FRAME_MIN = FRAME_MIN_TIGHT;
export const FRAME_MAX = FRAME_MAX_TIGHT;

/** Absolute safety ceiling per tier, applied after keyframe preservation. */
const ABSOLUTE_MAX_TIGHT    = 300;
const ABSOLUTE_MAX_EXPANDED = 1000;

/** Raw input beyond this size is treated as a runaway generator. */
const SAFETY_BAIL_THRESHOLD = 20_000;

// ── Per-category adaptive caps ────────────────────────────────────────────────
// Fine-grained frame budgets per category, overriding the binary tier split.
// Categories not listed here fall back to the tier-based resolver.

const CATEGORY_CAPS: Record<string, { min: number; max: number }> = {
  sorting:                 { min: 10,  max: 300  },
  search:                  { min: 10,  max: 200  },
  greedy:                  { min: 10,  max: 300  },
  math:                    { min: 10,  max: 200  },
  'number theory':         { min: 10,  max: 200  },
  'number-theory':         { min: 10,  max: 200  },
  graph:                   { min: 20,  max: 1000 },
  graphs:                  { min: 20,  max: 1000 },
  dp:                      { min: 15,  max: 800  },
  'dynamic programming':   { min: 15,  max: 800  },
  'dynamic-programming':   { min: 15,  max: 800  },
  string:                  { min: 15,  max: 600  },
  strings:                 { min: 15,  max: 600  },
  'string matching':       { min: 15,  max: 600  },
  'data structures':       { min: 15,  max: 1000 },
  'data-structures':       { min: 15,  max: 1000 },
  backtracking:            { min: 10,  max: 800  },
  geometry:                { min: 10,  max: 400  },
  geometric:               { min: 10,  max: 400  },
  sat:                     { min: 10,  max: 500  },
  tree:                    { min: 15,  max: 800  },
  trees:                   { min: 15,  max: 800  },
  hybrid:                  { min: 10,  max: 300  },
  'range tree':            { min: 10,  max: 400  },
  game:                    { min: 10,  max: 500  },
  'game theory':           { min: 10,  max: 500  },
  'game-theory':           { min: 10,  max: 500  },
};

/**
 * Returns the per-category cap if one exists, otherwise null (fall through to
 * the tier resolver).
 */
export function resolveCategoryCap(category: string | null | undefined): { min: number; max: number } | null {
  const cat = (category ?? '').toLowerCase().trim();
  return CATEGORY_CAPS[cat] ?? null;
}

// ── Input size warning ────────────────────────────────────────────────────────

/** Per-category max recommended input item counts. */
const INPUT_SIZE_LIMITS: Record<string, number> = {
  sorting:               64,
  search:                128,
  greedy:                64,
  hybrid:                64,
  'range tree':          200,
  game:                  50,
  'game theory':         50,
  'game-theory':         50,
  math:                  10_000,
  'number theory':       10_000,
  'number-theory':       10_000,
  graph:                 30,
  graphs:                30,
  dp:                    50,
  'dynamic programming': 50,
  'dynamic-programming': 50,
  string:                500,
  strings:               500,
  'string matching':     500,
  'data structures':     64,
  'data-structures':     64,
  backtracking:          15,
  geometry:              200,
  geometric:             200,
  sat:                   10,
  tree:                  64,
  trees:                 64,
};

/**
 * Returns a human-readable warning string if `itemCount` exceeds the
 * recommended limit for `category`, or null if within bounds.
 */
export function computeInputSizeWarning(
  category: string | null | undefined,
  itemCount: number,
): string | null {
  const cat   = (category ?? '').toLowerCase().trim();
  const limit = INPUT_SIZE_LIMITS[cat] ?? 64;
  if (itemCount <= limit) return null;
  return `Input size ${itemCount} exceeds the recommended max of ${limit} for ${category ?? 'this category'}. Very large inputs may produce thousands of steps and slow the visualizer.`;
}

// ── Frame budget descriptor ───────────────────────────────────────────────────

export interface FrameLimits {
  min:  number;
  max:  number;
  bail: number;
  tier: 'tight' | 'expanded';
}

// ── Category-level tier resolver ─────────────────────────────────────────────
/** Any algorithm in these categories gets the expanded tier. */
const EXPANDED_CATEGORIES = new Set<string>([
  'graph',
  'graphs',
  'dp',
  'dynamic programming',
  'dynamic-programming',
  'string',
  'strings',
  'string matching',
  'data structures',
  'data-structures',
  'backtracking',
  'game',
  'game theory',
  'game-theory',
  'range tree',
]);

// ── Resolver ──────────────────────────────────────────────────────────────────

/**
 * Determine the correct frame budget for an algorithm.
 * Pass the category string (e.g. 'Graph') as the primary classifier.
 * If category is null or unknown, default to TIGHT.
 */
export function resolveFrameLimits(_algorithmId?: string | null, category?: string | null): FrameLimits {
  const cat = (category ?? '').toLowerCase().trim();

  // Fine-grained per-category cap takes priority over the binary tier split
  const categoryCap = CATEGORY_CAPS[cat];
  if (categoryCap) {
    const tier: 'tight' | 'expanded' = EXPANDED_CATEGORIES.has(cat) ? 'expanded' : 'tight';
    return { min: categoryCap.min, max: categoryCap.max, bail: SAFETY_BAIL_THRESHOLD, tier };
  }

  const isExpanded = cat && EXPANDED_CATEGORIES.has(cat);
  if (isExpanded) {
    return {
      min:  FRAME_MIN_EXPANDED,
      max:  FRAME_MAX_EXPANDED,
      bail: SAFETY_BAIL_THRESHOLD,
      tier: 'expanded',
    };
  }

  return {
    min:  FRAME_MIN_TIGHT,
    max:  FRAME_MAX_TIGHT,
    bail: SAFETY_BAIL_THRESHOLD,
    tier: 'tight',
  };
}

// ── Internal helpers ──────────────────────────────────────────────────────────

function reindex(steps: ExecutionStep[]): ExecutionStep[] {
  const base = Date.now();
  return steps.map((s, i) => ({ ...s, stepId: i, timestamp: base + i }));
}

function isKeyframe(step: ExecutionStep): boolean {
  return (
    step.stepType === StepType.Swap         ||
    step.stepType === StepType.Update       ||
    step.stepType === StepType.StateChange  ||
    step.stepType === StepType.Decision     ||
    step.stepType === StepType.Completion   ||
    step.stepType === StepType.Error        ||
    step.stepType === StepType.Initialization
  );
}

/**
 * Uniform subsample: pick exactly `count` evenly spaced indices from [0, total-1],
 * always including first and last.
 */
function uniformSample(total: number, count: number): Set<number> {
  const keep = new Set<number>([0, total - 1]);
  if (count >= total) {
    for (let i = 0; i < total; i++) keep.add(i);
    return keep;
  }
  const step = (total - 1) / Math.max(count - 1, 1);
  for (let i = 0; i < count; i++) {
    keep.add(Math.min(total - 1, Math.round(i * step)));
  }
  return keep;
}

// ── Core clamping logic ───────────────────────────────────────────────────────

/**
 * Clamp any ExecutionStep array to the adaptive frame budget for `algorithmId`.
 *
 *  - TIGHT  tier:    [20 … 100]  frames  (sorting, search, simple math)
 *  - EXPANDED tier:  [25 … 1000] frames  (graph, DP, string, data-structures)
 *
 * Behaviour:
 *  • Steps ≤ min  → pad by repeating frames until minimum is met.
 *  • Steps in range → reindex only (no-op on count).
 *  • Steps > max  → keyframe-preserving downsample with gap-proportional fill.
 *  • Steps > bail → emergency uniform subsample to max (runaway guard).
 *  • Final hard cap: output is ALWAYS ≤ tier max.
 *
 * @param steps        Raw ExecutionStep array from local generator.
 * @param algorithmId  Registry id slug (e.g. 'dijkstra'). Used for tier lookup.
 * @param category     Category string (e.g. 'Graph'). Secondary tier lookup.
 */
export function clampExecutionSteps(
  steps:       ExecutionStep[],
  algorithmId?: string | null,
  category?:    string | null,
): ExecutionStep[] {
  if (!Array.isArray(steps) || steps.length === 0) return [];

  const limits = resolveFrameLimits(algorithmId, category);
  const { min: FRAME_MIN_L, max: FRAME_MAX_L, bail: BAIL } = limits;
  const ABSOLUTE_MAX_L = limits.tier === 'expanded' ? ABSOLUTE_MAX_EXPANDED : ABSOLUTE_MAX_TIGHT;

  // Emergency bail: runaway generator
  if (steps.length > BAIL) {
    console.warn(
      `[frameClamp] Runaway generator (${algorithmId ?? 'unknown'}): ` +
      `${steps.length} steps. Emergency subsample to ${FRAME_MAX_L}.`,
    );
    const keep = uniformSample(steps.length, FRAME_MAX_L);
    return reindex(steps.filter((_, i) => keep.has(i)));
  }

  // Already within the target window — reindex only
  if (steps.length >= FRAME_MIN_L && steps.length <= FRAME_MAX_L) {
    return reindex(steps);
  }

  // Too few steps — return as-is (no padding, better to have 5 meaningful
  // frames than 20 duplicates that make the animation look frozen)
  if (steps.length < FRAME_MIN_L) {
    return reindex(steps);
  }

  // Too many steps — intelligent keyframe-preserving downsample
  // Phase 1: collect keyframe indices (always preserved)
  const keyIndices = new Set<number>([0, steps.length - 1]);
  steps.forEach((s, i) => { if (isKeyframe(s)) keyIndices.add(i); });

  const sortedKeys = Array.from(keyIndices).sort((a, b) => a - b);

  // If keyframes alone already exceed the cap, fall back to uniform subsample
  if (sortedKeys.length >= FRAME_MAX_L) {
    const keep = uniformSample(steps.length, FRAME_MAX_L);
    return reindex(steps.filter((_, i) => keep.has(i)));
  }

  // Phase 2: fill remaining budget with gap-proportional non-keyframe frames
  const remainingSlots = FRAME_MAX_L - sortedKeys.length;
  const additions: number[] = [];

  if (remainingSlots > 0) {
    const gaps = sortedKeys.slice(1).map((next, i) => next - sortedKeys[i]!);
    const totalGap = gaps.reduce((sum, g) => sum + g, 0);
    if (totalGap > 0) {
      const slotsPerGap = gaps.map((gap) => Math.round((gap / totalGap) * remainingSlots));
      for (let i = 0; i < sortedKeys.length - 1; i++) {
        const start = sortedKeys[i]!;
        const end   = sortedKeys[i + 1]!;
        const count = slotsPerGap[i] ?? 0;
        for (let j = 1; j <= count; j++) {
          const index = Math.round(start + ((end - start) * j) / (count + 1));
          if (index > start && index < end) additions.push(index);
        }
      }
    }
  }

  let finalIndices = Array.from(new Set([...sortedKeys, ...additions])).sort((a, b) => a - b);

  // Hard cap: trim to absolute max for this tier
  if (finalIndices.length > ABSOLUTE_MAX_L) {
    const keep = uniformSample(finalIndices.length, ABSOLUTE_MAX_L);
    finalIndices = finalIndices.filter((_, i) => keep.has(i));
  }

  const keepSet = new Set(finalIndices);
  return reindex(steps.filter((_, i) => keepSet.has(i)));
}

/**
 * Hard limit for safety: never return more than the tier max, even if
 * keyframe preservation alone would exceed it.
 */
export function hardLimitExecutionSteps(
  steps:       ExecutionStep[],
  algorithmId?: string | null,
  category?:    string | null,
): ExecutionStep[] {
  const limits = resolveFrameLimits(algorithmId, category);
  const clamped = clampExecutionSteps(steps, algorithmId, category);
  if (clamped.length <= limits.max) return clamped;
  const keep = uniformSample(clamped.length, limits.max);
  return reindex(clamped.filter((_, i) => keep.has(i)));
}

/**
 * Limit raw input data size so that algorithms naturally produce a sane number
 * of frames. Only clamps bare arrays (sorting/search data) and nested arrays
 * inside tagged envelopes ({ type: 'array', data: [...] }). Structural object
 * properties like vertices/edges/nodes/points are left untouched.
 */
export function clampInputDataSize<T>(data: T, maxItems = 200): T {
  if (Array.isArray(data)) {
    return data.slice(0, maxItems) as unknown as T;
  }
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;

    // Tagged envelope: { type: 'array', data: [...] } — clamp the inner array
    if ('type' in obj && 'data' in obj && Array.isArray(obj['data'])) {
      return { ...obj, data: (obj['data'] as unknown[]).slice(0, maxItems) } as unknown as T;
    }

    // Otherwise return as-is — structural arrays (vertices, edges, points, etc.)
    // must NOT be truncated or the algorithm receives a broken graph/tree.
    return data;
  }
  return data;
}

export default clampExecutionSteps;
